import React from 'react';
import { CustomContainerWrapper, Thumbnail, ThingToDoComponentProps } from '.';
import { Box, Flex, IconV2, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@shared/joykit/packages/core/common';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useThingToDoCategoryUtils } from './hooks';
import { getDomainFromUrl } from './utils';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { hed6ToSubHeadingOverride, label2ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useThingsToDoTelemetry } from './ThingsToDo.telemetry';

export const CustomThingToDoComponent: React.FC<ThingToDoComponentProps> = ({ designType = 'fullRow', forceFullWidth = false, ...props }) => {
  const isMobile = useIsMobileScreen();
  const telemetry = useThingsToDoTelemetry();
  // force widget layout when mobile
  const designTypeResponsive = isMobile && designType === 'fullRow' ? 'widget' : designType;
  const { getCategoryTranslation, getCategoryIcon } = useThingToDoCategoryUtils();
  const hasUrl = !!props.url;
  const domain = hasUrl ? getDomainFromUrl(props.url!) : '';

  return (
    <CustomContainerWrapper
      as={hasUrl ? 'a' : 'div'}
      useShadow
      hasUrl={hasUrl}
      {...(hasUrl && {
        href: props.url,
        target: '_blank',
        rel: 'noopener sponsored nofollow',
        referrerPolicy: 'origin',
      })}
      designType={designTypeResponsive}
      forceFullWidth={forceFullWidth}
      onClick={() => {
        if (hasUrl) {
          telemetry.thingsToDoCTAClicked(props.url!, {
            componentName: 'CustomThingsToDo',
            url: props.url!,
            thingToDoName: props.title,
            city: props.location,
            tours: props.tours,
          });
        }
      }}
    >
      {props.image?.url && (
        <Flex
          as="figure"
          width={designTypeResponsive === 'fullRow' ? '50%' : '100%'}
          overflow="hidden"
          height={designTypeResponsive === 'fullRow' ? pxToRem(352) : pxToRem(148)}
        >
          <picture style={{ width: '100%', overflow: 'hidden' }}>
            <source srcSet={props.image.url} />
            <Thumbnail
              src={props.image.url}
              alt={props.title}
              height={designTypeResponsive === 'fullRow' ? pxToRem(352) : pxToRem(148)}
            />
          </picture>
        </Flex>
      )}
      <Flex
        flex={designTypeResponsive === 'widget' ? '1' : 'unset'}
        paddingX={pxToRem(24)}
        paddingY={pxToRem(16)}
        flexDirection="column"
        justifyContent="space-between"
        width={designTypeResponsive === 'fullRow' && props.image?.url ? '50%' : '100%'}
      >
        <Box>
          <Flex
            alignItems="center"
            gap={pxToRem(8)}
          >
            <IconV2>{getCategoryIcon(props.category || 'custom')}</IconV2>
            <TextV2 typographyVariant={'hed1'}>{getCategoryTranslation(props.category || 'custom')}</TextV2>
          </Flex>
          <Box
            marginBottom={pxToRem(16)}
            marginTop={pxToRem(8)}
          >
            <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
              <TextV2
                as="h2"
                typographyVariant="hed6"
              >
                {props.title}
              </TextV2>
            </GuestSiteTypographyOverride>
          </Box>
          <Box>
            <GuestSiteTypographyOverride override={label2ToParagraphOverride}>
              <TextV2
                typographyVariant="label2"
                marginTop={pxToRem(4)}
              >
                {props.description}
              </TextV2>
            </GuestSiteTypographyOverride>
          </Box>
        </Box>
        {domain && (
          <Box paddingTop={pxToRem(10)}>
            <TextV2
              typographyVariant="body1"
              truncate
            >
              {domain}
            </TextV2>
          </Box>
        )}
      </Flex>
    </CustomContainerWrapper>
  );
};
